import type { Paragraph } from '@/api/types/stowed-element'

/**
 * Structure-preserving text key: runs joined bare, lines by `\n`, paragraphs by a sentinel — so a
 * paragraph break is distinguishable from a line break, which is exactly what the seam probe has to
 * decide. The separator is a literal NUL, which never appears in user text.
 *
 * That NUL also makes `grep`/`rg`/`git diff` treat this WHOLE FILE as binary — a plain grep against
 * it prints "Binary file … matches" and no lines, i.e. it fails SILENTLY. Use `grep -a`. Known Gap
 * #36 ("do not hand-grep for dead code in this codebase") applies here with extra force.
 *
 * **Deliberately not `paragraphPlainText`** (`lib/text/paragraph-plain-text.ts`), which drops
 * `isHidden` lines. Two halves differing only in overset would compare equal under that one — and
 * after any save + reload every chain member's paragraphs are baked visual rows *plus* hidden
 * overset lines (`bakeShapeTextWireLines` writes them; `readShapeTextParagraphsFromDecoded` reads
 * them back). This counts them, matching the shape-text convention the fitter and splitter use.
 */
export function paragraphsKey(paragraphs: Paragraph[]): string {
  return paragraphs
    .map((paragraph) =>
      (paragraph.textLines ?? [])
        .map((line) => (line.textRuns ?? []).map((run) => run.textContent ?? '').join(''))
        .join('\n')
    )
    .join(' ')
}

export function keysMatch(a: Paragraph[], b: Paragraph[]): boolean {
  return paragraphsKey(a) === paragraphsKey(b)
}
